import ConfirmModal from "@app/components/confirm-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
  SystemMessage,
  BUCKET_URL_KEY,
} from "@app/constants";
import AffiliateCampaignService from "@app/services/http/affiliate-campaign.service";
import { openPortalDialog } from "@app/services/modal.service";
import {
  AffiliateCampaign,
  CampaignType,
  CampaignTypeOptions,
  Meta,
} from "@app/types";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import StorageService from "@core/services/storage/storage.service";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate } from "react-router-dom";
import CustomReactSelect from "@app/components/custom-react-select";

/* eslint-disable padding-line-between-statements */
function DateColumnTable({
  data,
  field,
}: {
  data: AffiliateCampaign;
  field: "startDate" | "endDate" | "createdDate";
}) {
  const formatDate = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === "") return "-";

    dayjs.extend(customParseFormat);

    const toVN = (d: dayjs.Dayjs) => d.format("DD/MM/YYYY");

    if (typeof value === "number") {
      const ms = value < 1e12 ? value * 1000 : value;
      const d = dayjs(ms);
      return d.isValid() ? toVN(d) : "-";
    }

    const trimmed = String(value).trim();
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      const ms = n < 1e12 ? n * 1000 : n;
      const d = dayjs(ms);
      return d.isValid() ? toVN(d) : "-";
    }

    let d = dayjs(trimmed, "DD/MM/YYYY", true);
    if (d.isValid()) return toVN(d);

    d = dayjs(trimmed);
    return d.isValid() ? toVN(d) : "-";
  };

  return (
    <div className="truncate max-w-[150px]">
      {formatDate((data as any)[field])}
    </div>
  );
}

function CampaignTypeColumnTable({ data }: { data: AffiliateCampaign }) {
  const getCampaignTypeName = (campaignType: number) => {
    const option = CampaignTypeOptions.find(
      (opt) => opt.value === campaignType
    );
    return option ? option.label : "-";
  };

  return (
    <div className="truncate max-w-[150px]">
      {getCampaignTypeName(data.type)}
    </div>
  );
}

function KasColumnTable({ data }: { data: AffiliateCampaign }) {
  return (
    <div className="truncate max-w-[100px]">
      {data.kas !== null && data.kas !== undefined ? data.kas : "-"}
    </div>
  );
}

function VipTimeColumnTable({ data }: { data: AffiliateCampaign }) {
  const formatVipTime = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return "-";

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days} ngày`;
    if (hours > 0) return `${hours} giờ`;
    if (minutes > 0) return `${minutes} phút`;
    return `${seconds} giây`;
  };

  return (
    <div className="truncate max-w-[120px]">{formatVipTime(data.vipTime)}</div>
  );
}

function ThumbnailColumnTable({ data }: { data: AffiliateCampaign }) {
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);

  if (!data.thumbnail) {
    return <div className="text-gray-400 text-center">-</div>;
  }

  return (
    <div className="flex justify-center items-center">
      <img
        src={s3Url + data.thumbnail}
        alt="Campaign"
        className="w-12 h-12 object-cover rounded border border-gray-200"
      />
    </div>
  );
}
/* eslint-enable padding-line-between-statements */

const getColumnConfig = (selectedCampaignType: number): ColumnConfig[] => {
  const baseColumns: ColumnConfig[] = [
    {
      label: "ID",
      dataKey: "id",
      width: selectedCampaignType === CampaignType.GIFT_BOOK ? "8%" : "10%",
      tdClassName: "truncate min-w-[60px] max-w-[80px]",
      hasOrder: true,
    },
  ];

  // Chỉ hiển thị Book ID và Book Name khi type search = GIFT_BOOK (1)
  if (selectedCampaignType === CampaignType.GIFT_BOOK) {
    baseColumns.push(
      {
        label: "Book ID",
        dataKey: "bookId",
        width: "10%",
        tdClassName: "truncate min-w-[80px] max-w-[100px]",
        hasOrder: true,
      },
      {
        label: "Book Name",
        dataKey: "bookName",
        width: "20%",
        tdClassName: "truncate min-w-[150px] max-w-[200px]",
        hasOrder: true,
      }
    );
  }

  baseColumns.push({
    label: "Type",
    dataKey: "type",
    width: selectedCampaignType === CampaignType.GIFT_BOOK ? "12%" : "15%",
    component: (props: any) => <CampaignTypeColumnTable {...props} />,
    hasOrder: true,
  });

  // Hiển thị nội dung của chiến dịch
  baseColumns.push({
    label: "Content",
    dataKey: "content",
    width: selectedCampaignType === CampaignType.GIFT_BOOK ? "20%" : "20%",
    // truncate long content with ellipsis
    component: (props: any) => (
      <div className="truncate max-w-[200px]">
        {props.data.content}
      </div>
    ),
    hasOrder: false,
  });

  // Chỉ hiển thị Kas khi type search = GIFT_KAS (2)
  if (selectedCampaignType === CampaignType.GIFT_KAS) {
    baseColumns.push({
      label: "Kas",
      dataKey: "kas",
      width: "12%",
      component: (props: any) => <KasColumnTable {...props} />,
      hasOrder: true,
    });
  }

  // Chỉ hiển thị VIP Time khi type search = GIFT_VIP (3)
  if (selectedCampaignType === CampaignType.GIFT_VIP) {
    baseColumns.push({
      label: "VIP Time",
      dataKey: "vipTime",
      width: "15%",
      component: (props: any) => <VipTimeColumnTable {...props} />,
      hasOrder: true,
    });
  }

  // Chỉ hiển thị Thumbnail khi type search = GIFT_KAS (2) hoặc GIFT_VIP (3)
  if (
    selectedCampaignType === CampaignType.GIFT_KAS ||
    selectedCampaignType === CampaignType.GIFT_VIP
  ) {
    baseColumns.push({
      label: "Thumbnail",
      dataKey: "thumbnail",
      width: "12%",
      headerClassName: "text-center",
      tdClassName: "flex justify-start items-center",
      component: (props: any) => <ThumbnailColumnTable {...props} />,
      hasOrder: false,
    });
  }

  baseColumns.push(
    {
      label: "Start Date",
      dataKey: "startDate",
      width: selectedCampaignType === CampaignType.GIFT_BOOK ? "15%" : "18%",
      component: (props: any) => (
        <DateColumnTable {...props} field="startDate" />
      ),
      hasOrder: true,
    },
    {
      label: "End Date",
      dataKey: "endDate",
      width: selectedCampaignType === CampaignType.GIFT_BOOK ? "15%" : "18%",
      component: (props: any) => <DateColumnTable {...props} field="endDate" />,
      hasOrder: true,
    },
    {
      label: "Create Date",
      dataKey: "createdDate",
      width: selectedCampaignType === CampaignType.GIFT_BOOK ? "15%" : "20%",
      component: (props: any) => (
        <DateColumnTable {...props} field="createdDate" />
      ),
      hasOrder: true,
    }
  );

  return baseColumns;
};

function AffiliateCampaignManagement() {
  const [affiliateCampaigns, setAffiliateCampaigns] = useState<
    AffiliateCampaign[]
  >([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();
  const [selectedCampaignType, setSelectedCampaignType] = useState<number>(
    CampaignType.GIFT_BOOK
  );

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  const navigate = useNavigate();

  useEffect(() => {
    const paramsWithType = {
      ...requestParams,
      type: selectedCampaignType,
    };

    AffiliateCampaignService.getList(
      paramsWithType,
      abortController.signal
    ).then((res) => {
      setAffiliateCampaigns(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams, selectedCampaignType]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onCampaignTypeChange = (value: number) => {
    setSelectedCampaignType(value);
    const newRequestParams: RequestParams = {
      ...requestParams,
      pageNum: DEFAULT_PAGE,
    };

    setRequestParams(newRequestParams);
  };

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    navigate(
      `/${RoutePath.AFFILIATE_CAMPAIGN_MANAGEMENT}/${RoutePath.CREATE_AFFILIATE_CAMPAIGN}`
    );
  };

  const handleEditClick = (affiliateCampaign: AffiliateCampaign) => {
    navigate(
      `/${RoutePath.AFFILIATE_CAMPAIGN_MANAGEMENT}/${RoutePath.UPDATE_AFFILIATE_CAMPAIGN}/${affiliateCampaign.id}`
    );
  };

  const handleDeleteClick = (affiliateCampaignId: number) => {
    if (affiliateCampaignId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_CAMPAIGN_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          AffiliateCampaignService.delete(affiliateCampaignId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách campaign</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-[#343C6A]">
            Danh sách affiliate campaign
          </h2>
          <div className="flex items-center gap-4">
            <div className="w-64">
              <CustomReactSelect
                options={CampaignTypeOptions}
                value={CampaignTypeOptions.find(
                  (opt) => opt.value === selectedCampaignType
                )}
                onChange={(value) =>
                  onCampaignTypeChange(value?.value as number)
                }
                placeholder="Chọn loại campaign"
                isClearable={false}
              />
            </div>
            <div className="w-80">
              <input
                type="text"
                placeholder="Tìm kiếm..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="mt-6">
          <Table
            columnConfig={getColumnConfig(selectedCampaignType)}
            dataTable={affiliateCampaigns}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default AffiliateCampaignManagement;
